import 'client-only';

import type { LogProvider } from '../types/log-provider';
import { ConsoleLogProvider } from './providers/console-log-provider';

/**
 * Client-side structured logging entry point.
 *
 * Returned `logInfo` / `logError` keep the bare call shape from the
 * original exports; every record's data bag carries `logger.name` so it
 * surfaces in PostHog and matches the server's pino chinding key.
 *
 * Error reporting (Sentry captureException) fans out from `logError`
 * via `observability/errors/report-error.client.ts`.
 */
const providers: LogProvider[] = [new ConsoleLogProvider()];

export function getLogger(module: string) {
  // Browser has no pino. Merge logger.name into the data bag
  // at wrapper time so it surfaces in PostHog and matches the
  // server's "logger.name" attribute key.
  const bindings = { 'logger.name': module };
  return {
    logModule: module,
    logInfo: (message: string, data?: Record<string, unknown>): void =>
      providers.forEach(p => p.info(message, { ...data, ...bindings })),
    logError: (
      message: string,
      error?: Error,
      extra?: Record<string, unknown>
    ): void => {
      const data = { ...extra, ...bindings };
      providers.forEach(p => p.error(message, error, data));

      const errorToReport = error ?? new Error(message);
      import('../errors/report-error.client')
        .then(({ reportError }) => reportError(message, errorToReport, data))
        .catch(() => {
          // Silently ignore error reporting failures
        });
    },
  };
}
